import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Route, Fuel, Gauge, AlertTriangle, Wallet, CalendarCheck, Droplets, Pencil, Satellite, FileOutput, Car, Camera, Wrench } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { resizeImage } from "../lib/img";
import { VehicleStatusDialog } from "../components/VehicleStatusDialog";
import { useAuth } from "../context/AuthContext";
import { VehicleFormDialog } from "../components/VehicleFormDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { fmtDate, fmtDateTime } from "../lib/format";

const STATUS = {
  tersedia: { label: "Tersedia", cls: "bg-[#198754] hover:bg-[#198754]" },
  digunakan: { label: "Digunakan", cls: "bg-[#10056E] hover:bg-[#10056E]" },
  penyelenggaraan: { label: "Penyelenggaraan", cls: "bg-sky-600 hover:bg-sky-600" },
  dilupus: { label: "Dilupus", cls: "bg-[#DC3545] hover:bg-[#DC3545]" },
};
const BSTATUS = {
  menunggu: { label: "Menunggu", cls: "bg-slate-500 hover:bg-slate-500" },
  diluluskan: { label: "Diluluskan", cls: "bg-[#198754] hover:bg-[#198754]" },
  ditolak: { label: "Ditolak", cls: "bg-[#DC3545] hover:bg-[#DC3545]" },
  selesai: { label: "Selesai", cls: "bg-[#10056E] hover:bg-[#10056E]" },
};

function Mini({ icon: Icon, label, value, tone = "bg-indigo-50 text-[#10056E]" }) {
  return (
    <Card className="border-slate-200 shadow-sm rounded-xl">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-lg font-bold leading-tight">{value}</p>
          <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground truncate">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "operator";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const photoRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const photo = await resizeImage(file, 700);
      const photo_thumb = await resizeImage(file, 160, 0.72);
      await api.put(`/vehicles/${id}/photo`, { photo, photo_thumb });
      toast.success("Foto kenderaan dikemas kini");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
    e.target.value = "";
  };

  const load = useCallback(() => {
    api.get(`/vehicles/${id}`).then((r) => setData(r.data)).catch(() => setError("Kenderaan tidak dijumpai"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-[#DC3545]">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;

  const { vehicle: v, stats: s, trips, bookings } = data;
  const trend = [...trips].reverse().filter((t) => t.kmpl).map((t) => ({ label: fmtDate(t.created_at), kmpl: t.kmpl }));

  return (
    <div className="space-y-6 fade-up" data-testid="vehicle-detail-page">
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/kenderaan")} data-testid="back-to-vehicles-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Kembali
        </Button>
        <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="vehicle-photo-input" />
        <div className="relative">
          {v.photo ? (
            <img src={v.photo} alt={v.registration_number} className="h-16 w-24 rounded-lg object-cover border shadow-sm" data-testid="vehicle-photo-img" />
          ) : (
            <span className="h-16 w-24 rounded-lg bg-slate-100 border flex items-center justify-center text-slate-400">
              <Car className="h-7 w-7" />
            </span>
          )}
          {canManage && (
            <button type="button" onClick={() => photoRef.current?.click()} data-testid="vehicle-photo-btn"
              className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-white border shadow flex items-center justify-center text-[#10056E] hover:bg-slate-50">
              <Camera className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight" data-testid="vehicle-detail-reg">{v.registration_number}</h1>
            <Badge className={STATUS[v.status]?.cls}>{STATUS[v.status]?.label}</Badge>
            <Badge variant="secondary">{v.category}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{v.make} {v.model} · {v.vehicle_type} · {v.fuel_type}</p>
        </div>
        {canManage && (
          <>
            <Button variant="outline" className="ml-auto border-amber-500 text-amber-700 hover:bg-amber-50" size="sm"
              onClick={() => setStatusOpen(true)} data-testid="vehicle-detail-status-btn">
              <Wrench className="h-4 w-4 mr-2" /> Tukar Status
            </Button>
            <Button variant="outline" className="border-[#10056E] text-[#10056E] hover:bg-indigo-50" size="sm"
              onClick={() => navigate(`/jana-laporan?vehicle=${v.id}`)} data-testid="vehicle-detail-jana-laporan-btn">
              <FileOutput className="h-4 w-4 mr-2" /> Jana Laporan
            </Button>
            <Button className="bg-[#10056E] hover:bg-[#2a17a8]" size="sm"
              onClick={() => setEditOpen(true)} data-testid="vehicle-detail-edit-btn">
              <Pencil className="h-4 w-4 mr-2" /> Kemas Kini
            </Button>
            <VehicleFormDialog open={editOpen} onOpenChange={setEditOpen} vehicle={v} onSaved={load} />
            <VehicleStatusDialog vehicle={v} open={statusOpen} onOpenChange={setStatusOpen} onDone={load} />
          </>
        )}
      </div>

      {v.status === "penyelenggaraan" && v.maintenance_info && (
        <div className="border border-amber-300 bg-amber-50 rounded-xl p-3 text-xs text-amber-900 flex flex-wrap gap-x-5 gap-y-1" data-testid="maintenance-info-banner">
          <b>Dalam Penyelenggaraan</b>
          {v.maintenance_info.jenis && <span>Jenis: <b>{v.maintenance_info.jenis}</b></span>}
          {v.maintenance_info.bengkel && <span>Bengkel: <b>{v.maintenance_info.bengkel}</b></span>}
          {v.maintenance_info.tarikh_masuk && <span>Masuk: <b>{v.maintenance_info.tarikh_masuk}</b></span>}
          {v.maintenance_info.jangkaan_siap && <span>Jangkaan siap: <b>{v.maintenance_info.jangkaan_siap}</b></span>}
          {v.maintenance_info.kos && <span>Kos: <b>RM {v.maintenance_info.kos}</b></span>}
          {v.maintenance_info.catatan && <span>Catatan: {v.maintenance_info.catatan}</span>}
        </div>
      )}
      {v.status === "dilupus" && v.disposal_info && (
        <div className="border border-slate-300 bg-slate-100 rounded-xl p-3 text-xs text-slate-700 flex flex-wrap gap-x-5 gap-y-1" data-testid="disposal-info-banner">
          <b>Telah Dilupus</b>
          {v.disposal_info.kaedah && <span>Kaedah: <b>{v.disposal_info.kaedah}</b></span>}
          {v.disposal_info.sebab && <span>Sebab: <b>{v.disposal_info.sebab}</b></span>}
          {v.disposal_info.rujukan && <span>Rujukan: <b>{v.disposal_info.rujukan}</b></span>}
          {v.disposal_info.tarikh && <span>Tarikh: <b>{v.disposal_info.tarikh}</b></span>}
          {v.disposal_info.catatan && <span>Catatan: {v.disposal_info.catatan}</span>}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Mini icon={Route} label="Jumlah Trip" value={s.jumlah_trip} />
        <Mini icon={Gauge} label="Jumlah Jarak" value={`${s.jumlah_jarak} km`} tone="bg-sky-50 text-sky-600" />
        <Mini icon={Droplets} label="Bahan Api" value={`${s.jumlah_liter} L`} tone="bg-teal-50 text-teal-600" />
        <Mini icon={Wallet} label="Kos Bahan Api" value={`RM ${s.jumlah_kos.toFixed(2)}`} tone="bg-amber-50 text-amber-600" />
        <Mini icon={Fuel} label="Purata Km/L" value={s.purata_kmpl} tone="bg-emerald-50 text-emerald-600" />
        <Mini icon={CalendarCheck} label="Tempahan Akan Datang" value={s.tempahan_akan_datang ?? 0} tone="bg-indigo-50 text-[#10056E]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Maklumat Kenderaan</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              {[
                ["No. Pendaftaran", v.registration_number],
                ["Jenama / Model", `${v.make} ${v.model}`],
                ["Jenis", v.vehicle_type],
                ["Kategori", v.category],
                ["Bahagian/Unit", v.unit_name || "-"],
                ["Bahan Api", v.fuel_type],
                ["Odometer Semasa", `${Number(v.current_odometer).toLocaleString()} km`],
                ["Geotab ID", v.geotab_id || "-"],
                ["Tempahan Akan Datang", s.tempahan_akan_datang],
                ["Didaftarkan", fmtDate(v.created_at)],
              ].map(([k, val]) => (
                <div key={k} className="flex justify-between gap-4 border-b last:border-0 pb-2 last:pb-0">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="font-semibold text-right">{val}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm rounded-xl lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Trend Km/Liter Kenderaan Ini</CardTitle>
            <CardDescription className="text-xs">Kadar Km/Liter setiap perjalanan</CardDescription>
          </CardHeader>
          <CardContent>
            {trend.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="kmpl" stroke="#10056E" strokeWidth={2.5} dot={{ r: 3, fill: "#10056E" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-16 text-center">Tiada data perjalanan untuk kenderaan ini.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trips">
        <TabsList>
          <TabsTrigger value="trips" data-testid="tab-vehicle-trips">Log Perjalanan ({trips.length})</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-vehicle-bookings">Sejarah Tempahan ({bookings.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="trips">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarikh</TableHead><TableHead>Staf</TableHead><TableHead>Destinasi</TableHead>
                  <TableHead>Jarak</TableHead><TableHead>Bahan Api</TableHead><TableHead>Km/L</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Tiada log perjalanan.</TableCell></TableRow>}
                {trips.map((t) => (
                  <TableRow key={t.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/log-perjalanan/${t.id}`)}>
                    <TableCell className="text-sm">{fmtDate(t.start_datetime)}</TableCell>
                    <TableCell className="text-sm font-semibold">{t.driver_name}</TableCell>
                    <TableCell className="text-sm">{t.destination}</TableCell>
                    <TableCell className="text-sm">{t.distance_km} km</TableCell>
                    <TableCell className="text-sm">{t.fuel_liters} L</TableCell>
                    <TableCell className="text-sm font-semibold">{t.kmpl ?? "-"}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#10056E] hover:bg-[#10056E]">
                        {t.kmpl != null ? `${t.kmpl} Km/L` : "Selesai"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="bookings">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tempoh</TableHead><TableHead>Staf</TableHead><TableHead>Destinasi</TableHead>
                  <TableHead>Tujuan</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Tiada tempahan.</TableCell></TableRow>}
                {bookings.map((b) => (
                  <TableRow key={b.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/tempahan/${b.id}`)}>
                    <TableCell className="text-sm">{fmtDateTime(b.start_datetime)} → {fmtDateTime(b.end_datetime)}</TableCell>
                    <TableCell className="text-sm font-semibold">{b.driver_name}</TableCell>
                    <TableCell className="text-sm">{b.destination}</TableCell>
                    <TableCell className="text-sm max-w-56 truncate">{b.purpose}</TableCell>
                    <TableCell><Badge className={BSTATUS[b.status]?.cls}>{BSTATUS[b.status]?.label}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
